import {
  forwardRef,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  FieldControl,
  FieldFooter,
  FieldLabel,
  FieldShell,
  getInputClassName,
  unlabeledAriaProps,
} from "../../field-ui";
import type { FormFieldInputProps, FormFieldRef } from "../../types";
import { reportFieldValue } from "../../../../lib/forms/reportFieldValue";
import {
  numberValidationMessage,
  type NumberFieldConfig,
} from "../../../../lib/forms/numberField";

interface NumberInputProps extends FormFieldInputProps {
  placeholder: string;
  labelMuted?: boolean;
  className?: string;
  required?: boolean;
  min?: number | null;
  max?: number | null;
  step?: number | null;
  integer?: boolean;
  validation?: (value: string) => boolean;
}

const NumberInput = forwardRef<FormFieldRef, NumberInputProps>((props, ref) => {
  const shellRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputId = useId();
  const [value, setValue] = useState(props.initialValue ?? "");
  const [validationState, setValidationState] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const config: NumberFieldConfig = {
    min: props.min ?? null,
    max: props.max ?? null,
    step: props.step ?? null,
    integer: Boolean(props.integer),
  };

  const commitValue = (next: string) => {
    setValue(next);
    reportFieldValue(props.fieldName, next, props.onValueChange);
  };

  const validate = () => {
    const message = numberValidationMessage(
      value,
      config,
      Boolean(props.required),
    );
    const fallbackOk = props.validation ? props.validation(value) : !message;
    const ok = message == null && fallbackOk;
    setValidationState(ok);
    setErrorMessage(ok ? null : message ?? "Укажите число");
    return ok;
  };

  useImperativeHandle(ref, () => ({
    setValidationState,
    value,
    fieldId: props.fieldId,
    fieldName: props.fieldName,
    scrollToElement: (options) => {
      shellRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        ...options,
      });
    },
    validate,
    clear,
  }));

  const clear = () => commitValue("");

  const stepAttr =
    config.step != null
      ? String(config.step)
      : config.integer
        ? "1"
        : "any";

  return (
    <FieldShell ref={shellRef} className={props.className}>
      <FieldLabel htmlFor={inputId} muted={props.labelMuted}>
        {props.placeholder}
      </FieldLabel>
      <FieldControl>
        <input
          ref={inputRef}
          id={inputId}
          value={value}
          onChange={(event) => {
            setValidationState(true);
            setErrorMessage(null);
            commitValue(event.target.value);
          }}
          onBlur={validate}
          type="number"
          inputMode={config.integer ? "numeric" : "decimal"}
          name={props.fieldName}
          autoComplete="off"
          min={config.min ?? undefined}
          max={config.max ?? undefined}
          step={stepAttr}
          aria-invalid={!validationState}
          {...unlabeledAriaProps(props.placeholder, props.accessibleName)}
          className={getInputClassName(validationState)}
        />
      </FieldControl>
      <FieldFooter>
        {!validationState ? (
          <span className="text-red-600" role="alert">
            {errorMessage ?? "Укажите число"}
          </span>
        ) : null}
      </FieldFooter>
    </FieldShell>
  );
});

NumberInput.displayName = "NumberInput";

export { NumberInput };
export type { NumberInputProps };
